import { sanitizeUrl } from '@braintree/sanitize-url';
import {
  Button,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useAuthContext } from 'src/auth/useAuthContext';
import { Upload, useUploadController } from 'src/components/upload';
import { useDebounce } from 'src/hooks/useDebounce';
import { useLocales } from 'src/locales';
import useUIState from 'src/state/ui/persistUiState';
import { isValidHttpUrl, isValidImageUrl } from 'src/utils/url';
import { useImageUpload } from './useImageUpload';

type Props = {
  handleImageSelect: (value: string, type: 'color' | 'custom') => void;
};

export const ImageUpload: React.FC<Props> = ({ handleImageSelect }) => {
  const [imageURL, setImageURL] = useState<string | null>('');
  const [isValidUrl, setValidUrl] = useState<boolean>(true);
  const [isUploading, setIsUploading] = useState(false);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const { addImageFile, getImageUrl } = useImageUpload();
  const { user } = useAuthContext();

  const debounceURL = useDebounce(imageURL, 1000);

  const { translate } = useLocales();
  const uploadControl = useUploadController();

  const { uploadedURLs, setUploadedURLs } = useUIState();

  // https://images.pexels.com/photos/3685837/pexels-photo-3685837.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isUploading) return;

    if (uploadControl.files.length === 0) return;

    onSubmit();
  }, [uploadControl.files]);

  useEffect(() => {
    const checkIsValid = async () => {
      let isValid = true;
      let url = '';

      if (debounceURL) {
        url = sanitizeUrl(debounceURL);
        if (isValidHttpUrl(url)) {
          isValid = await isValidImageUrl(url);
        } else {
          isValid = false;
        }
      }

      setValidUrl(isValid);

      if (!imageRef.current) return;

      imageRef.current.style.backgroundImage = `url("${url}")`;
    };

    checkIsValid().then();
  }, [debounceURL]);

  const onSubmit = async () => {
    try {
      setIsUploading(true);

      const picture = await addImageFile({
        file: uploadControl.files[0].file,
        parentId: user?.drive?.backgroundsFolderId || '',
      });

      const pictureResult = await getImageUrl(picture._id);

      handleImageSelect(pictureResult.url, 'custom');

      setIsUploading(false);
    } catch (error) {
      setIsUploading(false);
      console.error(error);
    }
  };

  const handleImageUrl = () => {
    if (!imageURL || !isValidUrl) return;
    handleImageSelect(imageURL, 'custom');
    setUploadedURLs([imageURL, ...uploadedURLs]);
  };

  return (
    <Stack px={2} py={3} gap={3} alignItems="center">
      <Stack width={1} gap={2}>
        <Typography variant="subtitle1">
          {translate('collaborate.docs.cover.use_image_url')}
        </Typography>
        <StyledImage
          ref={imageRef}
          className={cn({ invalid: !!imageURL && !isValidUrl })}
        >
          {(!imageURL || !isValidUrl) && (
            <Chip
              label={
                !isValidUrl
                  ? translate('collaborate.docs.cover.invalid_url')
                  : translate('collaborate.docs.cover.image_preview')
              }
              color={!isValidUrl ? 'error' : 'default'}
            />
          )}
        </StyledImage>
        <Stack direction="row" alignItems="center" gap={2}>
          <TextField
            name="name"
            type="text"
            label={translate('collaborate.docs.cover.image_url')}
            size="small"
            fullWidth
            value={imageURL}
            onChange={(e) => setImageURL(e.target.value)}
          />
          <Button
            onClick={handleImageUrl}
            variant="contained"
            disabled={!imageURL || !isValidUrl}
            sx={{ flexShrink: 0 }}
          >
            {translate('collaborate.docs.cover.use_url')}
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ width: '100%' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {translate('shared.labels.or')}
        </Typography>
      </Divider>

      <Stack width={1} gap={2} alignItems="center">
        <Upload
          uploadControl={uploadControl}
          accept={{ 'image/*': [] }}
          cover
          sx={{
            width: '100%',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
        <Typography variant="body2" sx={{ opacity: '0.60' }}>
          {translate('collaborate.docs.cover.image_description')}
        </Typography>
      </Stack>
    </Stack>
  );
};

const StyledImage = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  aspectRatio: '16 / 9',
  backgroundColor: theme.palette.background.paper,
  border: `2px solid ${theme.palette.divider}`,
  backgroundSize: 'cover',
  borderRadius: theme.shape.borderRadius,
  '&.invalid': {
    borderColor: theme.palette.error.main,
  },
}));
